// src/components/CompanyQuestionsList.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { CompanyQuestion } from '../types/companyQuestions';

interface CompanyQuestionsListProps {
  token: string;
}

const CompanyQuestionsList: React.FC<CompanyQuestionsListProps> = ({ token }) => {
  const [companyQuestions, setCompanyQuestions] = useState<CompanyQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanyQuestions();
  }, []);

  const fetchCompanyQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/company-questions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanyQuestions(response.data.companyQuestions || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch company questions');
      console.error('Error fetching company questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this company question set?')) {
      return;
    }

    try {
      await api.delete(`/company-questions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanyQuestions(companyQuestions.filter((cq) => cq.id !== id));
      alert('Company question deleted successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete company question');
      console.error('Error deleting company question:', err);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await api.put(
        `/company-questions/${id}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyQuestions(
        companyQuestions.map((cq) =>
          cq.id === id ? { ...cq, isActive: !currentStatus } : cq
        )
      );
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update status');
      console.error('Error updating status:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading company questions...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Company Questions Management</h2>
        <button
          onClick={() => navigate('/company-questions/new')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          + Create New
        </button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '5px',
            marginBottom: '20px',
          }}
        >
          {error}
        </div>
      )}

      {companyQuestions.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '50px',
            backgroundColor: '#f8f9fa',
            borderRadius: '5px',
          }}
        >
          <p>No company questions found. Click "Create New" to get started.</p>
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Knowledge Base</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Company Name</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Products</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Created</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {companyQuestions.map((cq) => (
              <tr key={cq.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>{cq.id}</td>
                <td style={{ padding: '12px' }}>
                  {cq.knowledgeBase?.name || `KB #${cq.kbId}`}
                </td>
                <td style={{ padding: '12px' }}>
                  {cq.questionsJson?.company_name || 'N/A'}
                </td>
                <td style={{ padding: '12px' }}>
                  {cq.questionsJson?.products?.length || 0} product(s)
                </td>
                <td style={{ padding: '12px' }}>
                  <button
                    onClick={() => handleToggleActive(cq.id, cq.isActive)}
                    style={{
                      padding: '5px 12px',
                      backgroundColor: cq.isActive ? '#28a745' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {cq.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td style={{ padding: '12px' }}>
                  {new Date(cq.createdAt).toLocaleDateString()}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button
                    onClick={() => navigate(`/company-questions/edit/${cq.id}`)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      marginRight: '5px',
                      fontSize: '12px',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(cq.id)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CompanyQuestionsList;
